import { FileDiff, MessageSquare, Lock } from 'lucide-react'
import { formatDate } from '@/lib/utils'
import type { ContractReviewComment, ContractReviewCommentType } from '@/types'

/**
 * The negotiation, as a thread.
 *
 * This is the record the whole feature exists to produce: each redline and each response in
 * order, so "what did we agree in round two" has an answer.
 *
 * A NEW COMPONENT RATHER THAN A REFACTOR. `TicketDetail.tsx` inlines roughly 400 lines of
 * comparable thread UI rather than extracting it, and that file is 2,000 lines with no test
 * coverage at all. Extracting a shared component is the better long-term answer and is
 * deliberately not being attempted here. Recorded as debt: if a third workflow needs a thread,
 * extract it then rather than writing a third copy.
 *
 * KIND IS CARRIED IN WORDS. A redline and a response are different in substance, not just in
 * tone, so each is labelled. Colour alone would fail WCAG 1.4.1 and, more practically, would be
 * unreadable in a printed or pasted copy of the negotiation.
 */

const KIND: Record<
  ContractReviewCommentType,
  { label: string; icon: typeof FileDiff; className: string }
> = {
  redline: {
    label: 'Redline',
    icon: FileDiff,
    className: 'border-l-2 border-l-primary bg-muted/40',
  },
  response: {
    label: 'Response',
    icon: MessageSquare,
    className: 'border-l-2 border-l-border bg-background',
  },
  note: {
    label: 'Note',
    icon: MessageSquare,
    className: 'border-l-2 border-l-transparent bg-muted/20',
  },
}

interface ReviewThreadProps {
  comments: ContractReviewComment[]
  /** Used only to label a comment as yours; it carries no permission meaning. */
  currentUserId?: string
}

export function ReviewThread({ comments, currentUserId }: ReviewThreadProps) {
  if (comments.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Nothing yet. The first redline or note will appear here.
      </p>
    )
  }

  return (
    // An ordered list, because the order IS the negotiation. A screen-reader user gets
    // "1 of 7" and so can tell where in the back-and-forth they are.
    <ol className="space-y-3" aria-label="Review thread, oldest first">
      {comments.map((comment, index) => {
        const kind = KIND[comment.comment_type] ?? KIND.note
        const Icon = kind.icon
        const isMine = Boolean(currentUserId) && comment.author_id === currentUserId

        return (
          <li key={comment.id} className={`rounded-r-md px-3 py-2 ${kind.className}`}>
            <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground mb-1">
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="font-medium text-foreground">{kind.label}</span>
              <span aria-hidden="true">·</span>
              <span>
                {comment.author_name || 'Unknown'}
                {isMine && <span className="text-muted-foreground"> (you)</span>}
              </span>
              <span aria-hidden="true">·</span>
              <span>{formatDate(comment.created_date)}</span>
              {comment.is_internal && (
                <span className="inline-flex items-center gap-1 rounded bg-warning px-1.5 py-0.5 text-warning-foreground">
                  <Lock className="h-3 w-3" aria-hidden="true" />
                  {/* Said in words. Somebody about to paste this into an email to the customer
                      needs to know, and a coloured chip alone would not tell them. */}
                  Internal only
                </span>
              )}
              <span className="sr-only">
                , entry {index + 1} of {comments.length}
              </span>
            </div>
            {/* whitespace-pre-wrap: redlines arrive as pasted clauses and their line breaks
                are meaningful. */}
            <p className="text-sm whitespace-pre-wrap break-words">{comment.body}</p>
          </li>
        )
      })}
    </ol>
  )
}
